import apiClient from './api';
import axios from 'axios';
import { Attachment } from '../types';

// 환경별 API 경로 설정
const getApiPath = () => {
  const baseUrl = process.env.REACT_APP_API_URL || `http://localhost:${process.env.REACT_APP_BACKEND_PORT || 3000}`;
  // 개발 환경에서는 /api를 추가, 운영 환경에서는 이미 포함되어 있음
  const isDevelopment = process.env.NODE_ENV === 'development' || baseUrl.includes('localhost');
  return isDevelopment ? `${baseUrl}/api` : baseUrl;
};

// 첨부파일 전용 API 클라이언트 (인증 없이 접근 가능)
const attachmentApiClient = axios.create({
  baseURL: getApiPath(),
  timeout: 30000, // 파일 다운로드는 시간이 오래 걸릴 수 있음
});

export interface UploadAttachmentRequest {
  file: File;
  lawId?: string;
  announcementId?: string;
  publicationId?: string;
  legislativeNoticeId?: string;
  isEditorImage?: boolean;
}

export const attachmentService = {
  // 파일 크기 포맷팅
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // 파일 업로드 (기존 방식 - 첨부파일용)
  async uploadAttachment(uploadData: UploadAttachmentRequest): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', uploadData.file);
    
    if (uploadData.lawId) {
      formData.append('lawId', uploadData.lawId);
    }
    if (uploadData.announcementId) {
      formData.append('announcementId', uploadData.announcementId);
    }
    if (uploadData.publicationId) {
      formData.append('publicationId', uploadData.publicationId);
    }
    if (uploadData.legislativeNoticeId) {
      formData.append('legislativeNoticeId', uploadData.legislativeNoticeId);
    }

    // 메뉴별 업로드 엔드포인트 결정
    let uploadEndpoint = '/attachments/upload';
    if (uploadData.lawId) {
      uploadEndpoint = '/attachments/upload/law';
    } else if (uploadData.announcementId) {
      uploadEndpoint = '/attachments/upload/announcement';
    } else if (uploadData.publicationId) {
      uploadEndpoint = '/attachments/upload/publication';
    } else if (uploadData.legislativeNoticeId) {
      uploadEndpoint = '/attachments/upload/legislative-notice';
    }

    const response = await apiClient.post<Attachment>(uploadEndpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // 에디터 이미지 업로드 (새로운 방식)
  async uploadEditorImage(
    file: File, 
    menuType: 'law' | 'announcement' | 'publication' | 'legislative-notice',
    entityId?: string
  ): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (entityId) {
      formData.append('entityId', entityId);
    }

    const response = await apiClient.post<Attachment>(
      `/attachments/upload-editor-image/${menuType}`, 
      formData, 
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // 법령별 첨부파일 조회 (에디터 이미지 제외, 인증 없이 접근 가능)
  async getLawAttachments(lawId: string): Promise<Attachment[]> {
    const response = await attachmentApiClient.get<Attachment[]>(`/attachments/law/${lawId}`);
    return response.data;
  },

  // 공지사항별 첨부파일 조회 (에디터 이미지 제외, 인증 없이 접근 가능)
  async getAnnouncementAttachments(announcementId: string): Promise<Attachment[]> {
    const response = await attachmentApiClient.get<Attachment[]>(`/attachments/announcement/${announcementId}`);
    return response.data;
  },

  // 공포·공시별 첨부파일 조회 (에디터 이미지 제외, 인증 없이 접근 가능)
  async getPublicationAttachments(publicationId: string): Promise<Attachment[]> {
    const response = await attachmentApiClient.get<Attachment[]>(`/attachments/publication/${publicationId}`);
    return response.data;
  },

  // 입법예고별 첨부파일 조회 (에디터 이미지 제외, 인증 없이 접근 가능)
  async getLegislativeNoticeAttachments(legislativeNoticeId: string): Promise<Attachment[]> {
    const response = await attachmentApiClient.get<Attachment[]>(`/attachments/legislative-notice/${legislativeNoticeId}`);
    return response.data;
  },

  // 첨부파일 삭제
  async deleteAttachment(attachmentId: string): Promise<void> {
    await apiClient.delete(`/attachments/${attachmentId}`);
  },

  // 첨부파일 다운로드 (인증 없이 접근 가능)
  async downloadAttachment(attachmentId: string): Promise<Blob> {
    const response = await attachmentApiClient.get(`/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // 첨부파일 직접 다운로드 (파일명 포함, 인증 없이 접근 가능)
  async downloadAttachmentDirect(attachmentId: string, fileName: string): Promise<void> {
    try {
      const response = await attachmentApiClient.get(`/attachments/${attachmentId}/download`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      throw new Error('파일 다운로드에 실패했습니다.');
    }
  },

  // 이미지 업로드 (기존 방식 유지)
  async uploadImage(file: File): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<Attachment>('/attachments/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // 메뉴별 이미지 업로드 (기존 방식 유지)
  async uploadMenuImage(file: File, menuType: 'law' | 'announcement' | 'publication' | 'legislative-notice'): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<Attachment>(`/attachments/upload-image/${menuType}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}; 